import { useMemo } from 'react';
import styles from './FilePreview.module.css';

export type DetectedFileType = 'pdf' | 'image' | 'unknown';
export type FileTypeHint = 'pdf' | 'image' | 'auto';
export type FilePreviewVariant = 'thumbnail' | 'inline' | 'modal';
export type FilePreviewSize = 'sm' | 'md' | 'lg';

export interface FilePreviewProps {
  /** URL לקובץ (Blob URL / Storage URL / data: URI) */
  fileUrl: string;
  /** שם להציג + alt text */
  fileName?: string;
  /** 'auto' → detect לפי extension; אחרת override */
  fileType?: FileTypeHint;
  /** אם קיים — מציג כפתור צפייה (👁) */
  onView?: () => void;
  /** הורדה (⬇) */
  onDownload?: () => void;
  /** עריכה (✎) */
  onEdit?: () => void;
  /** מחיקה (🗑) */
  onDelete?: () => void;
  /** thumbnail=card | inline=embedded גדול | modal=lightbox (1.D.3 minimal) */
  variant?: FilePreviewVariant;
  /** sm=80 | md=120 | lg=200 (thumbnail); אחר inline */
  size?: FilePreviewSize;
  className?: string;
}

const IMAGE_EXTENSIONS: readonly string[] = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
const PDF_EXTENSIONS: readonly string[] = ['pdf'];

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(' ');

const detectFileType = (url: string, hint: FileTypeHint): DetectedFileType => {
  if (hint === 'pdf' || hint === 'image') return hint;
  const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();
  const ext = cleanUrl.split('.').pop() || '';
  if (IMAGE_EXTENSIONS.includes(ext)) return 'image';
  if (PDF_EXTENSIONS.includes(ext)) return 'pdf';
  return 'unknown';
};

/**
 * תצוגת thumbnail/inline של קובץ עם 4 פעולות (R9).
 *
 * **Auto-detect** — בודק את ה-extension של ה-URL.
 * **PDF preview ב-inline** — שימוש ב-`<iframe>` בלי controls נוספים.
 *
 * @example
 * <FilePreview
 *   fileUrl="https://example.com/invoice-may.pdf"
 *   fileName="חשבונית מאי 2026.pdf"
 *   onView={() => openModal()}
 *   onDownload={() => download(url)}
 * />
 */
export const FilePreview = ({
  fileUrl,
  fileName,
  fileType = 'auto',
  onView,
  onDownload,
  onEdit,
  onDelete,
  variant = 'thumbnail',
  size = 'md',
  className,
}: FilePreviewProps): JSX.Element => {
  const detectedType = useMemo(() => detectFileType(fileUrl, fileType), [fileUrl, fileType]);

  const hasActions = onView || onDownload || onEdit || onDelete;

  return (
    <div
      className={cx(
        styles.root,
        styles[variant],
        styles[`size-${size}`],
        className
      )}
      data-testid="file-preview"
      data-file-type={detectedType}
    >
      <div className={styles.body}>
        {detectedType === 'image' && (
          <img
            src={fileUrl}
            alt={fileName || 'תצוגת קובץ'}
            className={styles.image}
            data-testid="file-preview-image"
          />
        )}
        {detectedType === 'pdf' && variant === 'inline' && (
          <iframe
            src={fileUrl}
            title={fileName || 'תצוגת PDF'}
            className={styles.iframe}
            data-testid="file-preview-iframe"
          />
        )}
        {detectedType === 'pdf' && variant !== 'inline' && (
          <div className={styles.iconBlock} data-testid="file-preview-pdf-icon">
            <span aria-hidden="true" className={styles.icon}>
              📄
            </span>
            <span className={styles.iconLabel}>PDF</span>
          </div>
        )}
        {detectedType === 'unknown' && (
          <div className={styles.iconBlock} data-testid="file-preview-unknown-icon">
            <span aria-hidden="true" className={styles.icon}>
              📎
            </span>
            <span className={styles.iconLabel}>קובץ</span>
          </div>
        )}
      </div>

      {fileName && (
        <div
          className={styles.fileName}
          title={fileName}
          data-testid="file-preview-filename"
        >
          {fileName}
        </div>
      )}

      {hasActions && (
        <div className={styles.actions} data-testid="file-preview-actions">
          {onView && (
            <button
              type="button"
              className={styles.actionBtn}
              onClick={onView}
              aria-label="צפייה בקובץ"
              data-testid="file-preview-view"
            >
              <span aria-hidden="true">👁</span>
            </button>
          )}
          {onDownload && (
            <button
              type="button"
              className={styles.actionBtn}
              onClick={onDownload}
              aria-label="הורד קובץ"
              data-testid="file-preview-download"
            >
              <span aria-hidden="true">⬇</span>
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              className={styles.actionBtn}
              onClick={onEdit}
              aria-label="ערוך קובץ"
              data-testid="file-preview-edit"
            >
              <span aria-hidden="true">✎</span>
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className={cx(styles.actionBtn, styles.actionBtnDanger)}
              onClick={onDelete}
              aria-label="מחק קובץ"
              data-testid="file-preview-delete"
            >
              <span aria-hidden="true">🗑</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
